/**
 * CERTIFIED YN — motion layer
 *
 * One rAF loop, one IntersectionObserver, no dependencies. Everything is
 * additive: if this file fails to load the site is still fully usable, just
 * static. Honours prefers-reduced-motion and defers to native scroll-driven
 * animations wherever the browser supports them.
 */

const REDUCED = matchMedia('(prefers-reduced-motion: reduce)');
const SUPPORTS_SDA = CSS.supports('animation-timeline: view()');

/* -------------------------------------------------------------------------
   Scroll bus — one listener, one rAF, read-then-write
   ---------------------------------------------------------------------- */

const bus = {
  y: window.scrollY,
  prevY: window.scrollY,
  velocity: 0,
  readers: new Set(),
  ticking: false,
};

function onScroll() {
  bus.y = window.scrollY;
  if (!bus.ticking) {
    bus.ticking = true;
    requestAnimationFrame(flush);
  }
}

function flush() {
  bus.ticking = false;
  const dy = bus.y - bus.prevY;
  bus.prevY = bus.y;
  // Smooth the velocity so a flick doesn't spike the belt speed.
  bus.velocity += (Math.abs(dy) - bus.velocity) * 0.18;
  for (const read of bus.readers) read(bus);
}

addEventListener('scroll', onScroll, { passive: true });
addEventListener('resize', onScroll, { passive: true });

/* -------------------------------------------------------------------------
   Reveal — hairline wipe as elements enter
   ---------------------------------------------------------------------- */

const revealObserver = new IntersectionObserver(
  (entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      entry.target.classList.add('is-in');
      revealObserver.unobserve(entry.target);
    }
  },
  { rootMargin: '0px 0px -12% 0px', threshold: 0.05 }
);

function registerReveals(root = document) {
  const targets = root.querySelectorAll('[data-cyn-reveal]:not(.is-in), .cyn-rule:not(.is-in), .cyn-lines:not(.is-in)');
  for (const el of targets) revealObserver.observe(el);
}

/**
 * Last resort. Reveal states start hidden, so anything the observer misses
 * would stay invisible forever. Sweep the document a few seconds in and
 * unhide whatever is on screen but still waiting.
 */
function revealSafetyNet() {
  const sweep = () => {
    for (const el of document.querySelectorAll('[data-cyn-reveal]:not(.is-in), .cyn-rule:not(.is-in)')) {
      const r = el.getBoundingClientRect();
      if (r.top < innerHeight && r.bottom > 0) el.classList.add('is-in');
    }
  };
  setTimeout(sweep, 2500);
  addEventListener('load', () => setTimeout(sweep, 400), { once: true });
}

/**
 * Stagger children of a group so a grid resolves as a cascade rather than
 * all at once. Delay is capped so long lists never feel slow.
 */
function stagger(root = document) {
  for (const group of root.querySelectorAll('[data-cyn-stagger]')) {
    const step = Number(group.dataset.cynStagger) || 70;
    const kids = group.querySelectorAll(':scope > *');
    kids.forEach((kid, i) => {
      kid.setAttribute('data-cyn-reveal', '');
      kid.style.setProperty('--d', `${Math.min(i * step, 480)}ms`);
    });
  }
}

/* -------------------------------------------------------------------------
   Line masks — split a heading into masked lines and lift them in
   ---------------------------------------------------------------------- */

function splitLines(el) {
  if (el.dataset.cynSplit === 'done') return;

  const source = el.textContent.trim().replace(/\s+/g, ' ');
  if (!source) return;

  const words = source.split(' ');
  el.textContent = '';
  const spans = words.map((word, i) => {
    const s = document.createElement('span');
    s.textContent = i === words.length - 1 ? word : `${word} `;
    el.appendChild(s);
    return s;
  });

  const lines = [];
  let currentTop = null;
  for (const span of spans) {
    const top = Math.round(span.getBoundingClientRect().top);
    if (currentTop === null || Math.abs(top - currentTop) > 2) {
      currentTop = top;
      lines.push([]);
    }
    lines[lines.length - 1].push(span.textContent);
  }

  el.textContent = '';
  el.classList.add('cyn-lines');
  lines.forEach((words_, i) => {
    const line = document.createElement('span');
    line.className = 'cyn-lines__line';
    const inner = document.createElement('span');
    inner.textContent = words_.join('').trim();
    inner.style.setProperty('--ld', `${i * 90}ms`);
    line.appendChild(inner);
    el.appendChild(line);
  });

  el.dataset.cynSplit = 'done';
}

function registerLineSplits(root = document) {
  for (const el of root.querySelectorAll('[data-cyn-lines]')) splitLines(el);
}

/* -------------------------------------------------------------------------
   Parallax — only when the browser has no native scroll timeline
   ---------------------------------------------------------------------- */

function initParallax() {
  if (SUPPORTS_SDA || REDUCED.matches) return;

  const items = [...document.querySelectorAll('[data-cyn-parallax]')];
  if (!items.length) return;

  let boxes = [];
  const measure = () => {
    boxes = items.map((el) => {
      const r = el.getBoundingClientRect();
      return { el, top: r.top + window.scrollY, height: r.height };
    });
  };
  measure();
  addEventListener('resize', measure, { passive: true });

  bus.readers.add(({ y }) => {
    const vh = innerHeight;
    for (const { el, top, height } of boxes) {
      const progress = (y + vh - top) / (vh + height); // 0 entering → 1 leaving
      if (progress < -0.2 || progress > 1.2) continue;
      const shift = (progress - 0.5) * (Number(el.dataset.cynParallax) || 12);
      el.style.setProperty('--py', `${shift}%`);
    }
  });
}

/* -------------------------------------------------------------------------
   Hero — the wordmark recedes as you leave it behind
   ---------------------------------------------------------------------- */

function initHero() {
  const bleed = document.querySelector('[data-cyn-hero-bleed]');
  if (!bleed || REDUCED.matches) return;

  bus.readers.add(({ y }) => {
    const p = Math.min(y / innerHeight, 1);
    bleed.style.setProperty('--hero-y', `${p * -14}%`);
    bleed.style.setProperty('--hero-s', `${1 + p * 0.18}`);
    bleed.style.opacity = `${1 - p * 0.85}`;
  });
}

/* -------------------------------------------------------------------------
   Manifest — words light up as the block crosses the viewport
   ---------------------------------------------------------------------- */

function initManifest() {
  const blocks = document.querySelectorAll('[data-cyn-manifest]');
  if (!blocks.length) return;

  for (const block of blocks) {
    const raw = block.textContent.trim().replace(/\s+/g, ' ');
    const accents = (block.dataset.cynAccent || '')
      .split(',')
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean);

    block.textContent = '';
    const words = raw.split(' ').map((word) => {
      const s = document.createElement('span');
      s.className = 'cyn-manifest__word';
      s.textContent = word;
      const bare = word.replace(/[^\p{L}\p{N}]/gu, '').toLowerCase();
      if (accents.includes(bare)) s.setAttribute('data-accent', '');
      block.append(s, document.createTextNode(' '));
      return s;
    });

    if (REDUCED.matches) {
      for (const w of words) w.setAttribute('data-lit', '');
      continue;
    }

    bus.readers.add(() => {
      const r = block.getBoundingClientRect();
      // Fully lit by the time the block sits mid-viewport.
      const progress = 1 - (r.top - innerHeight * 0.25) / (innerHeight * 0.55);
      const lit = Math.round(Math.max(0, Math.min(1, progress)) * words.length);
      words.forEach((w, i) => w.toggleAttribute('data-lit', i < lit));
    });
  }
}


/* -------------------------------------------------------------------------
   Sticky header state
   ---------------------------------------------------------------------- */

function initHeader() {
  const header = document.querySelector('header-component');
  if (!header) return;
  bus.readers.add(({ y }) => {
    header.toggleAttribute('data-cyn-stuck', y > 24);
  });
}

/* -------------------------------------------------------------------------
   Marquee reacts to scroll speed
   ---------------------------------------------------------------------- */

function initMarquee() {
  const belts = document.querySelectorAll('marquee-component');
  if (!belts.length || REDUCED.matches) return;

  bus.readers.add(({ velocity }) => {
    const boost = 1 + Math.min(velocity / 22, 2.2);
    for (const belt of belts) belt.style.setProperty('--marquee-boost', boost.toFixed(3));
  });

  // Decay back to rest when scrolling stops.
  setInterval(() => {
    bus.velocity *= 0.86;
    if (bus.velocity > 0.05) for (const read of bus.readers) read(bus);
  }, 100);
}

/* -------------------------------------------------------------------------
   Magnetic buttons — pointer only, never on touch
   ---------------------------------------------------------------------- */

function initMagnetic() {
  if (REDUCED.matches || !matchMedia('(any-pointer: fine)').matches) return;

  for (const el of document.querySelectorAll('[data-cyn-magnetic]')) {
    const strength = Number(el.dataset.cynMagnetic) || 0.28;

    el.addEventListener('pointermove', (e) => {
      const r = el.getBoundingClientRect();
      el.setAttribute('data-active', '');
      el.style.setProperty('--mx', `${(e.clientX - r.left - r.width / 2) * strength}px`);
      el.style.setProperty('--my', `${(e.clientY - r.top - r.height / 2) * strength}px`);
    });

    el.addEventListener('pointerleave', () => {
      el.removeAttribute('data-active');
      el.style.setProperty('--mx', '0px');
      el.style.setProperty('--my', '0px');
    });
  }
}

/* -------------------------------------------------------------------------
   Boot — and re-boot on Shopify section re-render / theme editor edits
   ---------------------------------------------------------------------- */

function boot(root = document) {
  registerLineSplits(root);
  stagger(root);
  registerReveals(root);
}

function init() {
  boot();
  initHero();
  initParallax();
  initManifest();
  initHeader();
  initMarquee();
  initMagnetic();
  revealSafetyNet();
  onScroll();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}

// Shopify swaps section HTML in the editor and on filter/paginate updates.
for (const evt of ['shopify:section:load', 'shopify:section:reorder']) {
  document.addEventListener(evt, (e) => boot(e.target));
}

// Anything the theme injects (filtered grids, quick-add modals) gets picked up.
new MutationObserver((records) => {
  for (const record of records) {
    for (const node of record.addedNodes) {
      if (node.nodeType === 1) boot(node);
    }
  }
}).observe(document.body, { childList: true, subtree: true });

export { boot };
